import React, { useState, useEffect } from 'react';
import { TextField, TextFieldProps } from '@mui/material';

interface CurrencyInputProps extends Omit<TextFieldProps, 'onChange' | 'value'> {
  value: number; // valor em centavos
  onChange: (value: number) => void; // callback com valor em centavos
  label?: string;
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
}

const CurrencyInput: React.FC<CurrencyInputProps> = ({
  value,
  onChange,
  label = 'Valor (R$)',
  helperText = 'Digite o valor em reais',
  disabled = false,
  required = false,
  fullWidth = true,
  size = 'small',
  ...textFieldProps
}) => {
  const [displayValue, setDisplayValue] = useState('');

  // Formata o valor para exibição
  const formatDisplayValue = (cents: number): string => {
    if (cents === 0) return '';

    const reais = cents / 100;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
    }).format(reais);
  };

  // Atualiza o valor de exibição quando o valor prop muda
  useEffect(() => {
    setDisplayValue(formatDisplayValue(value));
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Remove todos os caracteres não numéricos
    const rawValue = inputValue.replace(/\D/g, '');

    // Converte para centavos
    const cents = parseInt(rawValue || '0', 10);

    // Atualiza o valor de exibição
    setDisplayValue(inputValue);

    // Chama o callback com o valor em centavos
    onChange(cents);
  };

  const handleBlur = () => {
    // Formata o valor quando o campo perde o foco
    setDisplayValue(formatDisplayValue(value));
  };

  const handleFocus = () => {
    // Remove a formatação quando o campo ganha foco para facilitar a edição
    if (value > 0) {
      const reais = (value / 100).toFixed(2);
      setDisplayValue(reais);
    } else {
      setDisplayValue('');
    }
  };

  return (
    <TextField
      {...textFieldProps}
      label={label}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      fullWidth={fullWidth}
      disabled={disabled}
      required={required}
      size={size}
      helperText={helperText}
      onKeyDown={(e) => {
        if (
          (e.key.length === 1 && !/[0-9]/.test(e.key)) &&
          e.key !== "Backspace" &&
          e.key !== "Delete" &&
          e.key !== "ArrowLeft" &&
          e.key !== "ArrowRight" &&
          e.key !== "Tab"
        ) {
          e.preventDefault();
        }
      }}

      inputProps={{
        ...textFieldProps.inputProps,
        inputMode: 'decimal',
        pattern: '[0-9]*',
      }}
    />
  );
};

export default CurrencyInput;
